import { useState, useEffect, useCallback, useMemo } from "react";
import { getList } from "../../../api/getSimulationData";
import { TopListElement } from "../../../models/simulation/CommonTypes";
import { Systems } from "../../../enums/systems";
import { currencyFormatter } from "../../../utils/currencyFormatter";

export default function ProtokolTopList() {
  const [listElements, setListElements] = useState<TopListElement[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<number>(Systems.HPM);
  const [spinner, setSpinner] = useState(false);
  const [cache, setCache] = useState<{ [key: number]: TopListElement[] }>({});

  useEffect(() => {
    // Ha van cache-ben, ne fetch-elj újra
    if (cache[selectedSystem]) {
      setListElements(cache[selectedSystem]);
      return;
    }

    setSpinner(true);
    getList(selectedSystem)
      .then((res) => {
        setListElements(res);
        setCache((prev) => ({ ...prev, [selectedSystem]: res }));
        setSpinner(false);
      })
      .catch((err) => {
        console.error(err);
        setSpinner(false);
      });
  }, [selectedSystem, cache]);

  // ✅ Memoizált handler, hogy ne hozzon létre új függvényt minden renderkor
  const handleSystemChange = useCallback((system: number) => {
    setSelectedSystem(system);
  }, []);

  // ✅ Memoizált lista, ha például sortolnád
  const memoizedList = useMemo(() => {
    return [...listElements]; // ha szeretnél sortolni vagy szűrni, ide jöhet
  }, [listElements]);

  return (
    <div className="m-5 p-5 bg-gray-100 rounded-lg shadow-lg">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Toplista</h2>

      {spinner ? (
        <p className="text-center text-2xl animate-pulse">
          Kérlek várj még az adatok betöltődnek.
        </p>
      ) : (
        <>
          {/* System Selector */}
          <div className="mb-5 flex gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="system"
                value={Systems.HPM}
                checked={selectedSystem === Systems.HPM}
                onChange={() => handleSystemChange(Systems.HPM)}
                className="form-radio text-blue-500"
              />
              <span className="text-gray-700">HUMAN D.C.</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="system"
                value={Systems.ALT}
                checked={selectedSystem === Systems.ALT}
                onChange={() => handleSystemChange(Systems.ALT)}
                className="form-radio text-blue-500"
              />
              <span className="text-gray-700">BRAVE</span>
            </label>
          </div>

          {/* Top List */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-200 text-gray-700 text-left">
                  <th className="px-2 py-2">Dátum</th>
                  <th className="px-2 py-2">Név</th>
                  <th className="px-2 py-2">Befizetett (EUR)</th>
                  <th className="px-2 py-2">Befizetett (USD)</th>
                  <th className="px-2 py-2">Jelenlegi portfolio állás (USD)</th>
                  <th className="px-2 py-2">Birtokolt Bitcoin jelenlegi értéke (USD)</th>
                  <th className="px-2 py-2">Birtokolt Ethereum jelenlegi értéke (USD)</th>
                  
                  <th className="px-2 py-2">Fennmaradó eszközök értéke (USD)</th>
                  <th className="px-2 py-2">Fennmaradó eszközökből a birtokolt (Altcoin) (USD)</th>

                  <th className="px-2 py-2">Bitcoin árfolyam</th>
                  <th className="px-2 py-2">Ethereum árfolyam</th>
                  <th className="px-2 py-2">Várt BTC ár (100%)</th>
                  <th className="px-2 py-2">Várt ETH ár (100%)</th>
                  <th className="px-2 py-2">Kifizetve (EUR)</th>
                  <th className="px-2 py-2">Kifizetés dátuma</th>
                  <th className="px-2 py-2">Százalék</th>
                </tr>
              </thead>
              <tbody>
                {memoizedList.map((item, index) => (
                  <tr key={index} className="border-b last:border-b-0">
                    <td className="px-2 py-2">{new Date(item.date).toLocaleDateString()}</td>
                    <td className="px-2 py-2 font-semibold">{item.user?.name}</td>
                    <td className="px-2 py-2">{currencyFormatter(item.investedAmountEur, "EUR")}</td>
                    <td className="px-2 py-2">{currencyFormatter(item.investedAmountUsd, "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item.totalValue), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item.bitcoinPrice), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item.ethereumPrice), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item?.remainingPortfolioAssets), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item?.altCoins), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item.bitcoinExchangeRate), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item.ethereumExchangeRate), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item.expectedBitcoinExchangeRate), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(Number(item.expectedEthereumExchangeRate), "USD")}</td>
                    <td className="px-2 py-2">{currencyFormatter(item.payoutAmount, "EUR")}</td>
                    <td className="px-2 py-2">{item.payoutDate ? new Date(item.payoutDate).toLocaleDateString() : "Nincs"}</td>
                    <td
                      className={`px-2 py-2 font-semibold ${parseFloat(item.rate.toString()) > 0 ? "text-green-600" : "text-red-600"}`}
                    >
                      {item.rate.toString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
